import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5000/api',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

class TravelAgentService {
  // Complete registration with user ID and password
  async completeRegistrationWithUserId(userId, password) {
    try {
      const response = await api.post(`/travel-agent/travel-agent/${userId}/complete-registration`, {
        password
      })
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // Login travel agent
  async login(email, password) {
    try {
      const response = await api.post('/travel-agent/travel-agent-login', {
        email,
        password
      })

      const payload = response.data?.data
      // Backend returns JWT in the body; authMiddleware reads Bearer — same token all axios services use.
      if (payload?.token) {
        localStorage.setItem('token', payload.token)
      }
      if (payload?.travelAgent) {
        localStorage.setItem('user', JSON.stringify(payload.travelAgent))
        localStorage.setItem('logged_in', 'true')
      }
      
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  async register(data) {
    try {
      const response = await api.post('/travel-agent/register', {
        ...data,
        termsAccepted: data.termsAccepted !== false,
        extranetMisuseAcknowledgment: !!data.extranetMisuseAcknowledgment
      })
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  async verifyEmail(email, otp) {
    try {
      const response = await api.post('/travel-agent/verify-email', { email, otp })
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  async resendVerificationEmail(email) {
    try {
      const response = await api.post('/travel-agent/resend-otp', { email })
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  async getApplicationStatus(applicationId) {
    try {
      const response = await api.get(`/travel-agent/registration-status/${applicationId}`)
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  async changePassword(currentPassword, newPassword) {
    try {
      const response = await api.post('/travel-agent/change-password', {
        currentPassword,
        newPassword
      })
      const flags = response.data?.data
      if (flags) this.patchCachedUser(flags)
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // Get travel agent profile
  async getProfile() {
    try {
      const response = await api.get('/travel-agent/travel-agent-profile')
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // Update travel agent profile
  async updateProfile(profileData) {
    try {
      const response = await api.put('/travel-agent/travel-agent-profile', profileData)
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // Get bank account information
  async getBankAccount() {
    try {
      const response = await api.get('/travel-agent/bank-account')
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // Add bank account
  async addBankAccount(bankAccountData) {
    try {
      const response = await api.post('/travel-agent/bank-account', bankAccountData)
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // Update bank account
  async updateBankAccount(bankAccountData) {
    try {
      const response = await api.put('/travel-agent/bank-account', bankAccountData)
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // Remove bank account
  async removeBankAccount() {
    try {
      const response = await api.delete('/travel-agent/bank-account')
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // Verify bank account
  async verifyBankAccount(verificationCode) {
    try {
      const response = await api.post('/travel-agent/bank-account/verify', {
        verificationCode
      })
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }


  // Get travel agent data by user ID for registration completion
  async getTravelAgentById(userId) {
    try {
      const response = await api.get(`/travel-agent/travel-agent/${userId}`)
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // Request password reset
  async requestPasswordReset(email) {
    try {
      const response = await api.post('/travel-agent/password-reset/request', {
        email
      })
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // Reset password with token
  async resetPassword(token, newPassword) {
    try {
      const response = await api.post('/travel-agent/password-reset/reset', {
        token,
        newPassword
      })
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // Verify reset token
  async verifyResetToken(token) {
    try {
      const response = await api.get(`/travel-agent/password-reset/verify/${token}`)
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // Logout — calls backend to clear cookies, then wipes local state
  async logout() {
    try {
      await api.post('/travel-agent/logout')
    } catch (_) {
      // Even if backend call fails, clear local state
    } finally {
      localStorage.removeItem('user')
      localStorage.removeItem('logged_in')
      // Legacy cleanup — remove token if still present from old sessions
      localStorage.removeItem('token')
    }
  }

  // Get current user from localStorage
  getCurrentUser() {
    const user = localStorage.getItem('user')
    return user ? JSON.parse(user) : null
  }

  // Check if user is authenticated (cookie presence is inferred via logged_in flag)
  isAuthenticated() {
    return localStorage.getItem('logged_in') === 'true'
  }

  /**
   * Off-market stays catalogue — only when explicitly granted on the travel agent record.
   * Missing or false hasOffMarketAccess denies access.
   */
  hasOffMarketAccess() {
    const user = this.getCurrentUser()
    return user?.hasOffMarketAccess === true
  }

  mustChangePassword() {
    return this.getCurrentUser()?.mustChangePassword === true
  }

  /** Post-login destination */
  getPostLoginPath() {
    if (this.mustChangePassword()) return '/change-password-required'
    return '/dashboard'
  }

  /** Merge fields into the cached `user` object (e.g. after getProfile refreshes flags). */
  patchCachedUser(partial) {
    const user = this.getCurrentUser()
    if (!user || !partial || typeof partial !== 'object') return
    localStorage.setItem('user', JSON.stringify({ ...user, ...partial }))
  }

  // Handle API errors
  handleError(error) {
    if (error.response?.status === 404) {
      return {
        message: 'The requested resource was not found. Please check your link or contact support.',
        status: 404
      }
    }
    
    if (error.response?.status === 401) {
      return {
        message: 'Authentication failed. Please log in again.',
        status: 401
      }
    }
    
    if (error.response?.status === 403) {
      return {
        message: 'You do not have permission to access this resource.',
        status: 403
      }
    }
    
    if (error.response?.status === 422) {
      return {
        message: 'Invalid data provided. Please check your input and try again.',
        status: 422
      }
    }
    
    if (error.response?.data?.message) {
      return {
        message: error.response.data.message,
        status: error.response.status,
        errors: error.response.data.errors
      }
    }
    
    if (error.response?.status >= 500) {
      return {
        message: 'Server error occurred. Please try again later or contact support.',
        status: error.response.status
      }
    }
    
    if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
      return {
        message: 'Network connection error. Please check your internet connection and try again.',
        status: 0
      }
    }
    
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return {
        message: 'Request timed out. Please try again.',
        status: 0
      }
    }
    
    return {
      message: 'An unexpected error occurred. Please try again or contact support.',
      status: 500
    }
  }


  async getUserPaymentDetails() {
    try {
      const response = await api.get(`/cart/get-payment-method`);
      return response.data;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  }

  async getStripePaymentMethods() {
    try {
      const response = await api.get('/experience-checkout/payment-methods')
      return response.data
    } catch (error) {
      console.error('Error loading Stripe payment methods:', error)
      throw error
    }
  }

  async submitAgreement(agreementData) {
    try {
      const response = await api.put('/travel-agent/accept-agreement', agreementData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      return response.data
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to submit agreement')
    }
  }

    async updateCompanyDetails(formData) {
    try {
      const response = await api.put('/travel-agent/company-details', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      return response.data
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update company details')
    }
  }
}

export default new TravelAgentService()
